import type { DragControllerConfig, DragControllerState } from './types';
import type { Ref } from 'vue';

import { computed, reactive, ref } from 'vue';

import { DRAG_CONSTANTS, HEADER_OFFSET_CONFIG } from './constants';
import { DragState, MenuLevel } from './types';

export function useDragState(config: DragControllerConfig) {
	const { breakpoint = DRAG_CONSTANTS.DEFAULT_BREAKPOINT } = config;

	// Create reactive state object
	const state = reactive<DragControllerState>({
		currentState: DragState.IDLE,
		menuLevel: MenuLevel.CLOSED,
		currentY: 0,
		lastY: 0,
		isScreenBelowBreakpoint: false,
		isDragging: false,
		isInScrollMode: false,
		headerControlledByScroll: false
	});

	// Additional state refs
	const shouldFixOnBottom = ref(false);
	const lastWindowWidth = ref(0);
	const dragLimit = ref(0);

	// Access shared header drag offset state
	const headerDragOffsetY = useState<number>('header-drag-offset-y', () => 0);

	// State getters as computed properties
	const currentState = computed(() => state.currentState);
	const menuLevel = computed(() => state.menuLevel);
	const currentY = computed(() => state.currentY);
	const isDragging = computed(() => state.isDragging);
	const isInScrollMode = computed(() => state.isInScrollMode);
	const isScreenBelowBreakpoint = computed(() => state.isScreenBelowBreakpoint);

	// State transition functions
	function transitionTo(newState: DragState): void {
		state.currentState = newState;
	}

	function setMenuLevel(level: MenuLevel): void {
		state.menuLevel = level;
	}

	function setCurrentY(y: number): void {
		state.currentY = y;
		state.lastY = y;
	}

	function setDragging(dragging: boolean): void {
		state.isDragging = dragging;
		if (dragging) {
			transitionTo(DragState.DRAGGING);
		} else if (state.currentState === DragState.DRAGGING) {
			transitionTo(DragState.IDLE);
		}
	}

	function setScrollMode(inScrollMode: boolean): void {
		state.isInScrollMode = inScrollMode;
		if (inScrollMode) {
			transitionTo(DragState.SCROLLING);
			state.headerControlledByScroll = true;
		} else if (state.currentState === DragState.SCROLLING) {
			transitionTo(DragState.IDLE);
			state.headerControlledByScroll = false;
		}
	}

	function setMomentumScrolling(isActive: boolean): void {
		if (isActive) {
			transitionTo(DragState.MOMENTUM_SCROLLING);
		} else if (state.currentState === DragState.MOMENTUM_SCROLLING) {
			transitionTo(DragState.IDLE);
		}
	}

	function setHeaderControlledByScroll(controlled: boolean): void {
		state.headerControlledByScroll = controlled;
	}

	// Approximate drag distance (px) over which the navbar fully fades from
	// hidden → visible. Calibrated to feel right for the legacy WishlistView
	// drag UX; tuning this only changes how fast the header reveals.
	const HEADER_REVEAL_DRAG_RANGE_PX = 300;

	function updateHeaderOffsetForDrag(): void {
		if (!state.isScreenBelowBreakpoint || !state.isDragging) return;
		// currentY = 0 ⇒ fully open ⇒ navbar hidden (offset = MAX).
		// currentY grows as the drawer closes ⇒ navbar revealed (offset → MIN).
		const closeProgress = Math.min(
			1,
			state.currentY / HEADER_REVEAL_DRAG_RANGE_PX
		);
		headerDragOffsetY.value =
			(1 - closeProgress) * HEADER_OFFSET_CONFIG.MAX_OFFSET;
	}

	function checkScreenSize(): void {
		if (typeof window === 'undefined') {
			return;
		}

		state.isScreenBelowBreakpoint = window.innerWidth < breakpoint;

		if (window.innerWidth !== lastWindowWidth.value) {
			lastWindowWidth.value = window.innerWidth;

			if (!state.isScreenBelowBreakpoint) {
				resetState();
				// Reset header offset on desktop
				headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
			}
		}
	}

	function resetState(): void {
		transitionTo(DragState.IDLE);
		setMenuLevel(MenuLevel.CLOSED);
		setCurrentY(HEADER_OFFSET_CONFIG.MIN_OFFSET);
		setDragging(false);
		setScrollMode(false);
		shouldFixOnBottom.value = false;
		headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
		state.headerControlledByScroll = false;
	}

	function resetScrollModeIfNeeded(): void {
		if (state.isInScrollMode) {
			setScrollMode(false);
		}
	}

	// Can perform actions based on state
	function canPerformDrag(): boolean {
		return (
			state.isScreenBelowBreakpoint &&
			[DragState.IDLE, DragState.DRAGGING].includes(state.currentState)
		);
	}

	function canPerformScroll(): boolean {
		return (
			state.isScreenBelowBreakpoint &&
			[DragState.SCROLLING, DragState.MOMENTUM_SCROLLING].includes(
				state.currentState
			)
		);
	}

	function shouldHandleScrollInstead(): boolean {
		return state.isScreenBelowBreakpoint && state.isInScrollMode;
	}

	return {
		// State
		state: readonly(state),
		shouldFixOnBottom,
		headerDragOffsetY,

		// Computed getters
		currentState,
		menuLevel,
		currentY,
		isDragging,
		isInScrollMode,
		isScreenBelowBreakpoint,

		// State actions
		transitionTo,
		setMenuLevel,
		setCurrentY,
		setDragging,
		setScrollMode,
		setMomentumScrolling,
		setHeaderControlledByScroll,
		resetState,
		resetScrollModeIfNeeded,
		checkScreenSize,
		updateHeaderOffsetForDrag,

		// State queries
		canPerformDrag,
		canPerformScroll,
		shouldHandleScrollInstead
	};
}
